import React, { useState } from "react";

import type {
  PublicTrip,
  PublicTripV4
} from "@busbud/core-types/dist/lib/poll-service/api";
import Divider from "@busbud/design-system-components/dist/Divider";
import Heading from "@busbud/design-system-components/dist/Heading";
import IconButton from "@busbud/design-system-components/dist/IconButton";
import CrossSM from "@busbud/design-system-components/dist/Icons/Cross/SM";
import Stack from "@busbud/design-system-components/dist/Stack";
import Tab from "@busbud/design-system-components/dist/Tab";
import Tabs from "@busbud/design-system-components/dist/Tabs";

import { OUTBOUND_LEG_INDEX, RETURN_LEG_INDEX } from "@app/constants/trip";
import { useAppContext } from "@app/helpers/hooks";
import { getTripLegHasUnreliableDuration } from "@app/helpers/udm/get-leg-has-unreliable-duration";
import { getLegSegments } from "@app/helpers/udm/get-leg-segments";
import { isRoundtrip } from "@app/helpers/udm/is-roundtrip";
import { tripStopsGenerationFromPublicSegments } from "@app/helpers/udm/trip-stops-generation";
import { TripStopsFetchStatusKey } from "@app/modules/results/state/trip-stops-fetch-status";

import type { Props as ItineraryProps } from "../../itinerary/itinerary";

import { useStyles } from "./public-trip-itinerary.styles";
import { switchItineraryTabs } from "../../../tracking/itinerary-tracking";
import { Itinerary } from "../../itinerary/itinerary";

type TabName = "outbound" | "return";

interface Props {
  direction: "outbound" | "return";
  trip: PublicTrip | PublicTripV4;
  onClose: () => void;
}

export const PublicTripItinerary: React.FC<Props> = ({
  onClose,
  trip,
  direction
}) => {
  const { classes } = useStyles();
  const { tracker, translator } = useAppContext();
  const [active_tab, setActiveTab] = useState<TabName>(direction);

  const handleChange = (_: unknown, value: TabName) => {
    tracker.track(switchItineraryTabs(value));
    setActiveTab(value);
  };
  const leg_index =
    direction === "outbound" ? OUTBOUND_LEG_INDEX : RETURN_LEG_INDEX;
  const leg_segments = getLegSegments(trip, leg_index);
  const trip_stops_and_transfers =
    tripStopsGenerationFromPublicSegments(leg_segments);

  const itinerary_props: ItineraryProps = {
    direction,
    hide_time: getTripLegHasUnreliableDuration(leg_segments),
    segments: leg_segments,
    trip_info: trip,
    trip_stops_fetch_status: TripStopsFetchStatusKey.Idle,
    trip_stops_and_transfers
  };

  return (
    <>
      <Stack
        justifyContent="space-between"
        alignItems="center"
        className={classes.header}
      >
        <Heading size="sm">
          {translator.t("!departure.stops-and-transfers.title")}
        </Heading>
        <IconButton
          onClick={onClose}
          color="secondary"
          variant="text"
          size="xs"
        >
          <CrossSM color="icon.primary.opaque" />
        </IconButton>
      </Stack>
      <Divider />
      {isRoundtrip(trip) && (
        <Tabs
          variant="scrollable"
          orientation="horizontal"
          value={active_tab}
          onChange={handleChange}
        >
          <Tab
            label={translator.t("!terms.module.nav-tab-outbound")}
            value="outbound"
          />
          <Tab
            label={translator.t("!terms.module.nav-tab-return")}
            value="return"
          />
        </Tabs>
      )}
      <div className={classes.content}>
        <Itinerary {...itinerary_props} />
      </div>
    </>
  );
};
